import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

const VehicleSearchPage = () => {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recentSearches, setRecentSearches] = useState([]);
  const navigate = useNavigate();

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const savedSearches = localStorage.getItem("recentVehicleSearches");
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }

    // Check if we're coming back from job card details - preserve search state
    const navigationState = sessionStorage.getItem("vehicleSearchState");
    if (navigationState) {
      const state = JSON.parse(navigationState);
      setVehicleNumber(state.vehicleNumber || "");
      setSearchResults(state.searchResults || []);
      setError(state.error || "");
      // Clear the stored state so it doesn't persist on refresh
      sessionStorage.removeItem("vehicleSearchState");
    }
  }, []);

  // Save search to recent searches
  const saveToRecentSearches = (vehicleData) => {
    const searchItem = {
      vehicle_reg_no: vehicleData.vehicle_reg_no,
      model_name: vehicleData.model_name,
      brand_name: vehicleData.brand_name,
      customer_name: vehicleData.customer_name,
      searchTime: new Date().toISOString(),
    };

    const updatedSearches = [
      searchItem,
      ...recentSearches.filter(
        (item) => item.vehicle_reg_no !== vehicleData.vehicle_reg_no
      ),
    ].slice(0, 5); // Keep only last 5 searches

    setRecentSearches(updatedSearches);
    localStorage.setItem(
      "recentVehicleSearches",
      JSON.stringify(updatedSearches)
    );
  };

  // API call to search vehicle
  const searchVehicle = async (searchTerm = null) => {
    const searchValue = searchTerm || vehicleNumber;

    if (!searchValue.trim()) {
      setError("Please enter vehicle registration number");
      return;
    }

    setError("");

    try {
      const server_url =
        localStorage.getItem("server_url") || "https://your-domain.com/";
      const formData = new FormData();
      formData.append("vehicle_reg_no", searchValue.trim().toUpperCase());
      formData.append("company_id", "2");

      const response = await axios.post(
        `${server_url}search_vehicle.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status === "1") {
        setSearchResults(response.data.result);
        if (response.data.result.length > 0) {
          saveToRecentSearches(response.data.result[0]);
        }
      } else {
        setError(response.data.error || "Vehicle not found");
        setSearchResults([]);
      }
    } catch (err) {
      console.error("Search vehicle error:", err);
      setError("Failed to search vehicle. Please try again.");
      setSearchResults([]);
    } finally {
    }
  };

  const handleViewDetails = (vehicle) => {
    // Save current state before navigating
    const currentState = {
      vehicleNumber,
      searchResults,
      error,
    };
    sessionStorage.setItem("vehicleSearchState", JSON.stringify(currentState));
    navigate(`/job-card-details/${vehicle.vehicle_entry_id}`);
  };

  const handleClearSearch = () => {
    setVehicleNumber("");
    setSearchResults([]);
    setError("");
    // Clear stored state when manually clearing search
    sessionStorage.removeItem("vehicleSearchState");
  };

  const handleRecentSearch = (regNo) => {
    setVehicleNumber(regNo);
    // Direct search without setTimeout - this fixes the double click issue
    searchVehicle(regNo);
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };

  return (
    <div className="history-container">
      {/* Sticky Search Section */}
      <div
        className="sticky-filter"
        style={{ position: "fixed", top: "56px", width: "100%" }}
      >
        <div className="home-icon">
          <div className="section-home">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                searchVehicle();
              }}
              className="search-form"
            >
              <div className="input-group same-line">
                <div className="input-wrapper" style={{ flex: 1 }}>
                  <span className="input-icon">🔍</span>
                  <input
                    type="text"
                    value={vehicleNumber}
                    onChange={(e) =>
                      setVehicleNumber(e.target.value.toUpperCase())
                    }
                    placeholder="Enter vehicle plate number"
                    className="search-input"
                    style={{ paddingLeft: "40px" }}
                  />
                  {vehicleNumber && (
                    <button
                      type="button"
                      className="clear-btn"
                      onClick={handleClearSearch}
                      style={{
                        position: "absolute",
                        right: "10px",
                        background: "none",
                        border: "none",
                        fontSize: "16px",
                        color: "#666",
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="search-btn"
                  disabled={loading || !vehicleNumber.trim()}
                >
                  {loading ? (
                    <div className="loading-spinner-small"></div>
                  ) : (
                    <i className="fas fa-search"></i>
                  )}
                </button>
              </div>
            </form>

            {error && (
              <div
                className="error-message"
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  background: "#fee",
                  color: "#c33",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              >
                <span className="error-icon">⚠️</span> {error}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area - Adjusted for sticky filter */}
      <div className="content-area">
        {/* Results Count */}

        {searchResults.length > 0 && (
          <div className="heading-section">
            <div
              className="sa-title popcat"
              style={{ marginTop: "20px", width: "70%" }}
            >
              Vehicle Search
            </div>
            {/* {searchResults.length > 0 && (
              <div className="vehicle-count">
                {searchResults.length} records
              </div>
            )} */}
          </div>
        )}

        {/* Recent Searches */}
        {recentSearches.length > 0 &&
          !searchResults.length &&
          !loading &&
          !vehicleNumber && (
            <div className="home-vehicles">
              <div className="section-home">
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#2d3748",
                    marginBottom: "15px",
                    textAlign: "left",
                  }}
                >
                  Recent Searches
                </div>
                <div className="vehicles-list home-icon">
                  {recentSearches.map((search, index) => (
                    <div
                      key={index}
                      className="vehicle-card section-home"
                      onClick={() => handleRecentSearch(search.vehicle_reg_no)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="vehicle-header">
                        <div style={{ width: "100%" }}>
                          <table style={{ width: "100%", textAlign: "left" }}>
                            <tbody>
                              <tr>
                                <td
                                  style={{
                                    width: "50%",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    color: "#0352a1",
                                  }}
                                >
                                  {search.vehicle_reg_no}
                                </td>
                                <td
                                  style={{
                                    textAlign: "right",
                                    fontWeight: "bold",
                                    color: "#2c3e50",
                                  }}
                                >
                                  {search.customer_name}
                                </td>
                              </tr>
                              <tr>
                                <td
                                  style={{
                                    width: "50%",
                                    color: "#666",
                                    fontSize: "14px",
                                  }}
                                >
                                  {search.brand_name} {search.model_name}
                                </td>
                                <td
                                  style={{
                                    textAlign: "right",
                                    color: "#666",
                                    fontSize: "12px",
                                  }}
                                >
                                  Tap to search →
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        {/* Search Results */}
        <div className="home-vehicles">
          {loading ? (
            <div className="loading-vehicles">
              <div className="loading-spinner"></div>
              <div>Searching vehicles...</div>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="vehicles-list home-icon">
              {searchResults.map((vehicle, index) => (
                <div key={index} className="vehicle-card section-home">
                  <div className="vehicle-header">
                    <div
                      style={{ width: "100%" }}
                      onClick={() => handleViewDetails(vehicle)}
                    >
                      <table style={{ width: "100%", textAlign: "left" }}>
                        <tbody>
                          {/* Customer Name and Mobile */}
                          <tr>
                            <td
                              style={{
                                width: "50%",
                                fontWeight: "bold",
                                fontSize: "16px",
                              }}
                            >
                              {vehicle.customer_name || "N/A"}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                color: "#0352a1",
                              }}
                            >
                              {vehicle.customer_mobile || "N/A"}
                            </td>
                          </tr>

                          {/* Vehicle Details */}
                          <tr>
                            <td style={{ width: "50%", color: "#2c3e50" }}>
                              {vehicle.vehicle_reg_no || "N/A"}
                            </td>
                            <td style={{ textAlign: "right", color: "#666" }}>
                              {vehicle.brand_name || ""}{" "}
                              {vehicle.model_name || ""}
                            </td>
                          </tr>

                          {/* Service Details */}
                          <tr>
                            <td
                              style={{
                                width: "50%",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              Estimate: #{vehicle.vehicle_entry_no}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                color: "#27ae60",
                              }}
                            >
                              {formatAmount(vehicle.vehicle_entry_total_amt)}
                            </td>
                          </tr>

                          {/* Entry Date */}
                          <tr>
                            <td
                              style={{
                                width: "50%",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              Entry: {vehicle.vehicle_entry_in_date}{" "}
                              {vehicle.vehicle_entry_in_time}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                fontSize: "14px",
                                color:
                                  vehicle.payment_status == 1
                                    ? "#27ae60"
                                    : "#e74c3c",
                              }}
                            >
                            
                              {vehicle.payment_status == 1 ? (
                                "Paid"
                              ) : (
                                <>
                                  <a
                                    href={`https://wa.me/91${vehicle.customer_mobile.replace(
                                      /[^0-9]/g,
                                      ""
                                    )}?text=Hello ${
                                      vehicle.customer_name
                                    },%0A%0AThis is a gentle reminder regarding the pending payment for your vehicle service.%0A%0A🚗 Vehicle No: ${
                                      vehicle.vehicle_reg_no
                                    }%0A📅 Service Date: ${new Date(
                                      vehicle.vehicle_entry_date
                                    ).toLocaleDateString()}%0A💰 Amount Due: ${
                                      formatAmount(
                                        vehicle.vehicle_entry_total_amt
                                      ) || "0"
                                    }%0A%0AKindly complete the payment at your convenience.%0AThank you 🙏`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#e74c3c",
                                      textDecoration: "none",
                                      fontSize: "11px",
                                      padding: "4px 8px",
                                      border: "1px solid #e74c3c",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <i className="fab fa-whatsapp"></i>
                                  </a>{" "}
                                  Unpaid
                                </>
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td
                              colSpan={2}
                              style={{
                                width: "50%",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              Exit: {vehicle.vehicle_entry_out_date}{" "}
                              {vehicle.vehicle_entry_out_time}
                            </td>
                          </tr>

                          {/* Remarks */}
                          {vehicle.vehicle_entry_remark && (
                            <tr>
                              <td
                                colSpan="2"
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  fontStyle: "italic",
                                  paddingTop: "8px",
                                }}
                              >
                                📝 {vehicle.vehicle_entry_remark}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : vehicleNumber && !error ? (
            <div className="no-vehicles section-home">
              <div className="no-vehicles-icon">🔍</div>
              <div className="no-vehicles-title">No Service Records Found</div>
            </div>
          ) : !vehicleNumber ? (
            <div className="no-vehicles section-home">
              <div className="no-vehicles-icon">🚗</div>
              <div className="no-vehicles-title">Search Vehicle History</div>
              <div className="no-vehicles-text">
                Enter a vehicle registration number to search for service
                records
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <style jsx>{`
        .history-container {
          min-height: 100vh;
          background: white;
          padding: 0;
        }

        /* Sticky Filter Styles */
        .sticky-filter {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .content-area {
          padding-top: 70px; /* Adjusted for sticky filter height */
        }

        /* Search/Filters Section */
        .home-icon .section-home {
          padding: 8px 12px 12px 12px;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }

        .input-group.same-line {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .input-group.same-line .search-input {
          flex: 1;
        }

        .input-wrapper {
          position: relative;
          flex: 1;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 12px;
          font-size: 16px;
          color: #666;
          z-index: 1;
        }

        .search-input {
          padding: 12px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          background: #f8f9fa;
          width: 100%;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #0352a1;
          background: white;
          box-shadow: 0 0 0 3px rgba(3, 82, 161, 0.1);
        }

        .date-separator {
          color: #718096;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        .search-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 13px 15px;
          min-width: 50px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .search-btn:hover:not(:disabled) {
          background: #023e7d;
          transform: translateY(-1px);
        }

        .search-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .loading-spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Heading Section */
        .heading-section {
          display: ruby;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding: 0 20px;
        }

        .sa-title {
          font-size: 18px;
          font-weight: 600;
          color: #2d3748;
        }

        .vehicle-count {
          color: #ffffff;
          width: auto;
          background-color: #0352a1;
          font-size: 14px;
          font-weight: 500;
          padding: 5px 12px;
          border-radius: 50px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Vehicles List */
        .home-vehicles {
          padding: 0px 10px;
        }

        .loading-vehicles {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        .vehicles-list {
          display: grid;
          gap: 15px;
        }

        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #0352a1;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          background: #e8f4ff;
        }

        .vehicle-header {
          width: 100%;
        }

        .vehicle-header table {
          width: 100%;
          border-collapse: collapse;
        }

        .vehicle-header td {
          padding: 4px 0;
          vertical-align: top;
        }

        /* No Vehicles State */
        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 20px 0;
        }

        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
          opacity: 0.5;
        }

        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .content-area {
            padding-top: 65px;
          }

          .heading-section {
            padding: 0 15px;
          }

          .home-vehicles {
            padding: 10px 10px;
          }

          .vehicle-card {
            padding: 12px;
          }

          .vehicle-header td {
            font-size: 14px;
          }

          .input-group.same-line {
            flex-direction: row;
          }

          .date-separator {
            font-size: 12px;
          }

          .vehicle-count {
            font-size: 12px;
            padding: 4px 10px;
          }

          .sticky-filter {
            padding: 6px 0;
          }
        }

        @media (max-width: 480px) {
          .input-group.same-line {
            flex-wrap: wrap;
          }

          .search-input {
            min-width: 120px;
            font-size: 12px;
          }

          .date-separator {
            display: none;
          }

          .heading-section {
            flex-direction: column;
            gap: 8px;
            align-items: flex-start;
          }

          .vehicle-count {
            align-self: flex-end;
          }

          .sticky-filter {
            padding: 4px 0;
          }

          .vehicle-header td {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default VehicleSearchPage;
